<template>
  <!-- <v-container> -->
    <v-row v-if="selectedStore=='DEPO'" style="margin-top: 10px;">
      <v-col cols="12" md="6">
        <v-select
          v-model="selectedQuantity"
          :items="quantityOptions"
          label="Sélectionner un compteur"
          variant="outlined"
          density="compact"
          clearable
        >
          <template #item="{ props, item }">
            <v-list-item v-bind="props">
              <template #title>
                {{ formatLabel(item.title) }}
              </template>
            </v-list-item>
          </template>
        </v-select>
      </v-col>
      
      
    </v-row>
    
    <v-card class="d-flex justify-center" v-if="selectedStore=='DEPO'">
      <v-pie
        :title="`${selectedQuantityLabel} de ${props.data.barcode ? props.data.barcode : props.data.id}`"
        hide-slice
        :inner-cut="53"
        animation
        :palette="['#048BA8', '#99C24D', '#F18F01', '#D65A4B', '#6A4C93', '#F9A03F', '#2D6A4F', '#E63946', '#1D3557', '#457B9D']"
        :size="250"
        tooltip
        reveal
        :items="pieItems"
      > 
        <template v-slot:center>
          <div class="text-center">
            <div class="text-display-medium">{{ totalValue }}</div>
            <div class="opacity-70 mt-1 mb-n1">Total</div>
          </div>
        </template>
      </v-pie>
    </v-card>
    
    <!-- Informations supplémentaires -->
    <v-row v-if="resaleRate !== null" class="mt-4">
      <v-col cols="12">
        <v-card variant="outlined" class="pa-4">
          <v-row>
            <v-col cols="12" md="4">
              <div class="text-caption text-medium-emphasis">Ventes totales</div>
              <div class="text-h6">{{ resaleData.totalSales }}</div>
              <div class="text-caption text-grey">
                {{`${props.quantityLabels.frontOfficeSalesQuantity.libelle} + ${props.quantityLabels.customerDeliveryQuantity.libelle} + ${props.quantityLabels.customerInvoiceQuantity.libelle}`}}
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-medium-emphasis">Approvisionnement total</div>
              <div class="text-h6">{{ resaleData.totalSupply }}</div>
              
              <div class="d-flex flex-wrap ga-1 mt-1">
                <v-chip
                  v-for="(value, key) in resaleData.etabSupply"
                  :key="key"
                  size="x-small"
                  variant="outlined"
                  class="ma-1"
                >
                  {{ `${key} : ${value}` }}
                </v-chip>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="text-caption text-medium-emphasis">Taux de revente</div>
              <v-chip 
                :color="resaleRate > 1 ? 'success' : resaleRate > 0.5 ? 'warning' : 'error'"
                size="large"
              >
                {{ (resaleRate * 100).toFixed(2) }}%
              </v-chip>
              <div class="text-caption text-grey mt-1">
                {{ getRateInterpretation() }}
              </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  <!-- </v-container> -->
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { VPie } from 'vuetify/labs/VPie';

const props = defineProps({
  data: {
    type: Object,
    required: true,
    validator: (value) => {
      return value && value.id && value.stores && Array.isArray(value.stores);
    }
  },
  quantityLabels: {
    type: Object,
    required: true
  },
  selectedQuantityPrime: {
    type: String,
    required: true
  },
  selectedStore: {
    type: String,
    required: true
  }
});
// console.log('props.data',props);
// ============ ÉTAT ============
const selectedQuantity = ref('frontOfficeSalesQuantity');
const pieItems = ref([]);
const totalValue = ref(0);
const resaleRate = ref(null);
const resaleData = ref({
  totalSales: 0,
  totalSupply: 0,
  etabSupply: {}
});

// ============ COMPUTED ============
// Options pour le sélecteur
const quantityOptions = computed(() => {
  if (!props.data?.stores?.[0]?.warehouses?.[0]?.quantities) {
    return [];
  }
  return Object.keys(props.data.stores[0].warehouses[0].quantities);
});

// Label formaté
const selectedQuantityLabel = computed(() => {
  return formatLabel(selectedQuantity.value);
});

// ============ FONCTIONS ============
function formatLabel(key) {
  if (!key) return '';
  return props.quantityLabels[key]?.libelle || key;
}

function getSupplyStoreLabel() {
  const store = props.selectedStore;
  if (store === 'DEPO') return 'tous les établissements';
  return store;
}

function getRateInterpretation() {
  if (resaleRate.value === null) return 'Données insuffisantes';
  if (resaleRate.value > 1.2) return '✅ Excellent taux';
  if (resaleRate.value > 0.8) return '👍 Bon taux';
  if (resaleRate.value > 0.4) return '⚠️ taux moyenne';
  return '🔴 Faible taux';
}

// ============ CALCUL DU TAUX DE REVENTE ============
function calculateResaleRate() {
  if (!props.data || !props.data.stores) {
    resaleRate.value = null;
    resaleData.value = { totalSales: 0, totalSupply: 0, etabSupply: {} };
    return;
  }
  
  let totalSales = 0;
  let totalSupply = 0;
  const etabSupply = {};
  
  // Établissements pour lesquels on calcule l'approvisionnement
  let supplyStores = [];
  const selectedStore = props.selectedStore;
  
  if (selectedStore === 'DEPO') {
    // DEPO : prendre tous les établissements
    supplyStores = props.data.stores.map(s => s.id);
  } else if (['NEGO', 'NGEXT', 'LO07'].includes(selectedStore)) {
    // NEGO, NGEXT, LO07 : prendre uniquement l'établissement sélectionné
    supplyStores = [selectedStore];
  } else {
    // Autres cas : prendre tous les établissements
    supplyStores = props.data.stores.map(s => s.id);
  }
  
  props.data.stores.forEach(store => {
    store.warehouses.forEach(warehouse => {
      const quantities = warehouse.quantities;
      
      // Calcul des ventes (TOUS les établissements)
      const sales = (quantities.frontOfficeSalesQuantity || 0) +
                    (quantities.customerInvoiceQuantity || 0) +
                    (quantities.customerDeliveryQuantity || 0);
      if (supplyStores.includes(store.id)) {
        totalSales += sales;
      
        // Calcul de l'approvisionnement (selon selectedStore)
      
        const supply = quantities.supplierDeliveryQuantity || 0;
        totalSupply += supply;
        
        // Stocker par établissement
        if (supply !== 0) {
          if (etabSupply[store.id]) {
            etabSupply[store.id] += supply;
          } else {
            etabSupply[store.id] = supply;
          }
        }
      }
    });
  });
  
  resaleData.value = {
    totalSales: totalSales,
    totalSupply: totalSupply,
    etabSupply: etabSupply
  };
  
  // Calcul du taux de revente
  if (totalSupply !== 0) {
    resaleRate.value = totalSales / totalSupply;
  } else {
    resaleRate.value = null;
  }
}

// ============ MISE À JOUR DU PIE ============
function updatePieData() {
  if (!props.data || !props.data.stores) {
    pieItems.value = [];
    totalValue.value = 0;
    return;
  }
  
  // Construire les données pour le Pie
  const items = props.data.stores
    .map(store => {
      let total = 0;
      store.warehouses.forEach(warehouse => {
        const quantity = warehouse.quantities[selectedQuantity.value] || 0;
        if(selectedQuantity.value=='transferQuantity' && store.id=='DEPO'){
          return
        }
        // else{
          total += Math.abs(quantity);
        // }
        
      });
      return {
        storeId: store.id,
        total: total
      };
    })
    .filter(item => item.total !== 0)
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
  
  totalValue.value = items.reduce((sum, item) => sum + Math.abs(item.total), 0);
  pieItems.value = items.map((item, index) => ({
    key: index + 1,
    title: item.storeId,
    value: item.total
  }));
  
  // Calculer le taux de revente
  calculateResaleRate();
}

// ============ WATCHERS ============
function getSelectedQuantity() {
  selectedQuantity.value = props.selectedQuantityPrime;
}

watch(() => props.selectedQuantityPrime, getSelectedQuantity);
watch(selectedQuantity, updatePieData);
watch(() => props.data, updatePieData, { deep: true });
watch(() => props.selectedStore, updatePieData);

// ============ INITIALISATION ============
onMounted(() => {
  getSelectedQuantity();
  updatePieData();
});
</script>

<style scoped>
.v-pie {
  min-height: 300px;
}
</style>